/**
 * Fiche équipe — modale affichant le profil d'une équipe.
 *
 * En détente : composition + compétitions.
 * En compétition : composition + calendrier (matchs) + contacts capitaine/lieutenant.
 * Sur smartphone (compact), s'affiche en plein écran.
 */

import { useState, useEffect } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { useProfile } from '../contexts/ProfileContext';
import { useResponsive } from '../hooks/useResponsive';
import '../styles/profile-modal.css';
import { BACKEND_URL } from '../config';

const OBSOLETE_CONTACT_TYPES = new Set(['msn', 'icq', 'skype']);

interface TeamProfileModalProps {
  teamId: string;
  compet: boolean;
  onClose: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TeamData = Record<string, any>;

function fmtNum(v: unknown): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString('fr-FR');
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  try {
    return new Date(String(v)).toLocaleDateString('fr-FR');
  } catch {
    return String(v);
  }
}

// Ancienneté de l'équipe (ex. "créée le 18/08/2007 · 18 ans")
function fmtAge(v: unknown): string | null {
  if (!v) return null;
  try {
    const created = new Date(String(v));
    if (isNaN(created.getTime())) return null;
    const years = Math.floor((Date.now() - created.getTime()) / (365.25 * 24 * 3600 * 1000));
    const yearsLabel = years >= 1 ? ` · ${years} an${years > 1 ? 's' : ''}` : '';
    return `Créée le ${created.toLocaleDateString('fr-FR')}${yearsLabel}`;
  } catch {
    return null;
  }
}

function fmtDatetime(v: unknown): string {
  if (!v) return '-';
  try {
    const d = new Date(String(v));
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(v);
  }
}

const ROLE_ORDER: Record<string, number> = {
  capitaine: 0,
  lieutenant: 1,
  attaquant: 2,
  def_E: 3,
  def_N: 4,
  def_O: 5,
  remplacant1: 6,
  remplacant2: 7,
  remplacant3: 8,
  remplacant4: 9,
  'remplaçant': 10,
};

function roleLabel(role: string): string {
  switch (role) {
    case 'capitaine': return 'Capitaine';
    case 'lieutenant': return 'Lieutenant';
    case 'attaquant': return 'Attaquant';
    case 'def_E': case 'def_N': case 'def_O': return 'Défenseur';
    default:
      if (role.startsWith('rempl')) return 'Remplaçant';
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
}

export default function TeamProfileModal({ teamId, compet, onClose }: TeamProfileModalProps) {
  const [data, setData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('composition');
  const { isCompact } = useResponsive();

  const tabs = compet
    ? [
        { id: 'composition', label: 'Composition' },
        { id: 'calendrier', label: 'Calendrier' },
        { id: 'championnat', label: 'Championnat' },
      ]
    : [
        { id: 'composition', label: 'Composition' },
        { id: 'competitions', label: 'Compétitions' },
      ];

  useEffect(() => {
    setLoading(true);
    setError(null);
    setActiveTab('composition');

    const url = `${BACKEND_URL}/api/profiles/team/${encodeURIComponent(teamId)}?compet=${compet}`;
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) throw new Error(json.error || 'Erreur inconnue');
        setData(json.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [teamId, compet]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="profile-overlay" onClick={handleOverlayClick}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="profile-header">
          {isCompact && (
            <button className="profile-close" onClick={onClose} title="Retour">
              ←
            </button>
          )}
          <PlayerAvatar
            pseudo={data?.nom || teamId}
            size={48}
            photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(data?.nom || teamId)}?size=96`}
          />
          <div className="profile-header-info">
            <div className="profile-pseudo">{data?.nom || `Équipe #${teamId}`}</div>
            {data?.devise && <div className="profile-team-devise">{data.devise}</div>}
            {data?.date && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fmtAge(data.date)}</div>
            )}
          </div>
          {!isCompact && (
            <button className="profile-close" onClick={onClose} title="Fermer">×</button>
          )}
        </div>

        {/* Tabs */}
        {data && tabs.length > 1 && (
          <div className="profile-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="profile-body">
          {loading && (
            <div className="profile-loading">
              <div className="profile-spinner" />
              <span>Chargement...</span>
            </div>
          )}

          {error && (
            <div className="profile-error">
              <span>Impossible de charger l'équipe</span>
              <span style={{ fontSize: 12 }}>{error}</span>
            </div>
          )}

          {data && !loading && !error && (
            <>
              {activeTab === 'composition' && <CompositionTab data={data} compet={compet} />}
              {activeTab === 'calendrier' && compet && <CalendrierTab data={data} />}
              {activeTab === 'championnat' && compet && <ChampionnatTab data={data} />}
              {activeTab === 'competitions' && !compet && <CompetitionsTab data={data} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================
   Onglet Composition
   ============================================ */

function CompositionTab({ data, compet }: { data: TeamData; compet: boolean }) {
  const { openPlayerProfile } = useProfile();

  const members = (data.members || []).slice().sort(
    (a: { role: string }, b: { role: string }) =>
      (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)
  );

  // Contacts capitaine/lieutenant (compétition)
  const contacts = compet ? data.contacts : null;
  const filteredContacts: { role: string; entries: [string, string][] }[] = [];
  if (contacts) {
    for (const role of ['capitaine', 'lieutenant']) {
      const c = contacts[role];
      if (!c?.contacts) continue;
      const entries = Object.entries(c.contacts as Record<string, string | null>)
        .filter(([type, val]) => val && !OBSOLETE_CONTACT_TYPES.has(type)) as [string, string][];
      if (entries.length > 0) {
        filteredContacts.push({ role, entries });
      }
    }
  }

  return (
    <>
      <div className="profile-section">
        <h3 className="profile-section-title">Membres</h3>
        <div className="profile-member-list">
          {members.map((m: { pseudo: string; pseudo_display?: string; role: string }) => (
            <div
              key={m.pseudo}
              className="profile-member-item"
              onClick={() => openPlayerProfile(m.pseudo)}
            >
              <PlayerAvatar pseudo={m.pseudo} size={32} />
              <div className="profile-member-info">
                <span className="profile-member-pseudo">{m.pseudo_display || m.pseudo}</span>
                <span className="profile-member-role">{roleLabel(m.role)}</span>
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div className="profile-empty">Aucun membre</div>
          )}
        </div>
      </div>

      {filteredContacts.length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section-title">Contacts</h3>
          {filteredContacts.map(({ role, entries }) => (
            <div key={role} style={{ marginBottom: 12 }}>
              <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 6px', textTransform: 'capitalize' }}>
                {role}
              </h4>
              <div className="profile-contacts">
                {entries.map(([type, val]) => (
                  <div key={type} className="profile-contact-item">
                    <span className="profile-contact-type">
                      {type === 'wt' ? 'Messagerie WT' : type === 'email' ? 'Email' : type}
                    </span>
                    <span>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ============================================
   Onglet Calendrier (compétition)
   ============================================ */

const OUTCOME_LABELS: Record<string, string> = { win: 'Victoire', draw: 'Nul', loss: 'Défaite' };

function opponentLabel(adversaire: number, adversaireNom: string | null | undefined): string {
  if (adversaire === 2 || !adversaireNom) return 'Entraînement';
  return adversaireNom;
}

function CalendrierTab({ data }: { data: TeamData }) {
  const nextMatches = data.next_matches || [];
  const lastMatches = data.last_matches || [];

  return (
    <>
      {nextMatches.length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section-title">Prochains défis</h3>
          <div className="profile-match-list">
            {nextMatches.map((m: {
              id: number; rdv: string; adversaire: number; adversaire_nom?: string | null;
              nbparties: number; etat: string; kind_label?: string;
            }) => (
              <div key={m.id} className="profile-match-item">
                <div>
                  <div className="profile-match-opponent">vs {opponentLabel(m.adversaire, m.adversaire_nom)}</div>
                  <div className="profile-match-date">
                    {fmtDatetime(m.rdv)} · {m.nbparties} partie{m.nbparties > 1 ? 's' : ''}
                    {m.kind_label ? ` · ${m.kind_label}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                  {m.etat}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {lastMatches.length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section-title">Derniers défis</h3>
          <div className="profile-match-list">
            {lastMatches.map((m: {
              id: number; quand: string; adversaire: number; adversaire_nom?: string | null;
              pm_for: number; pm_against: number; outcome: string; kind_label?: string;
            }) => (
              <div key={m.id} className="profile-match-item">
                <div>
                  <div className="profile-match-opponent">vs {opponentLabel(m.adversaire, m.adversaire_nom)}</div>
                  <div className="profile-match-date">
                    {fmtDate(m.quand)}{m.kind_label ? ` · ${m.kind_label}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <span className={`profile-match-score ${m.outcome}`}>
                    {fmtNum(m.pm_for)} - {fmtNum(m.pm_against)}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: m.outcome === 'win' ? 'var(--success, #2e9e5b)' : m.outcome === 'loss' ? 'var(--danger, #d9534f)' : 'var(--text-muted)',
                    }}
                  >
                    {OUTCOME_LABELS[m.outcome] || m.outcome}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {nextMatches.length === 0 && lastMatches.length === 0 && (
        <div className="profile-empty">Aucun défi récent</div>
      )}
    </>
  );
}

/* ============================================
   Onglet Championnat (compétition)
   ============================================ */

interface ChampionshipPhaseItem {
  phase: { id: number; nom: string; etat: string; debut: string; fin: string };
  division: { id: number; nom: string; index: number; hierarchie: number; taille: number } | null;
  classement: number | null;
  point: number | null;
  victoires: number;
  nuls: number;
  defaites: number;
  forfaits: number;
  joues: number;
}

function ChampionnatTab({ data }: { data: TeamData }) {
  const championship = data.championship as { current?: ChampionshipPhaseItem | null; items?: ChampionshipPhaseItem[] } | null;
  const current = championship?.current || null;
  const items = championship?.items || [];

  if (!championship || items.length === 0) {
    return <div className="profile-empty">Aucune participation au championnat par équipes</div>;
  }

  return (
    <>
      {current && (
        <div className="profile-section">
          <h3 className="profile-section-title">Phase en cours</h3>
          <div className="profile-match-item">
            <div>
              <div className="profile-match-opponent">{current.division?.nom || 'Division inconnue'}</div>
              <div className="profile-match-date">
                {current.phase.nom} · {fmtDate(current.phase.debut)} – {fmtDate(current.phase.fin)}
              </div>
            </div>
            <span className="profile-match-score" style={{ fontWeight: 700 }}>
              {current.classement ? `${fmtNum(current.classement)}${current.division?.taille ? `e/${current.division.taille}` : 'e'}` : '—'}
            </span>
          </div>
        </div>
      )}

      <div className="profile-section">
        <h3 className="profile-section-title">Historique par phase</h3>
        <table className="profile-stats-table">
          <thead>
            <tr>
              <th>Phase</th>
              <th>Division</th>
              <th style={{ textAlign: 'right' }}>Classement</th>
              <th style={{ textAlign: 'right' }}>Pts</th>
              <th style={{ textAlign: 'right' }}>V</th>
              <th style={{ textAlign: 'right' }}>N</th>
              <th style={{ textAlign: 'right' }}>D</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.phase.id}>
                <td className="stat-label">
                  {it.phase.nom}
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>
                    {fmtDate(it.phase.debut)} – {fmtDate(it.phase.fin)}
                  </span>
                </td>
                <td className="stat-label">{it.division?.nom || '—'}</td>
                <td className="stat-value">
                  {it.classement ? `${fmtNum(it.classement)}${it.division?.taille ? `/${it.division.taille}` : ''}` : '—'}
                </td>
                <td className="stat-value">{fmtNum(it.point)}</td>
                <td className="stat-value">{fmtNum(it.victoires)}</td>
                <td className="stat-value">{fmtNum(it.nuls)}</td>
                <td className="stat-value">{fmtNum(it.defaites)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ============================================
   Onglet Compétitions (détente)
   ============================================ */

function CompetitionsTab({ data }: { data: TeamData }) {
  const competitions = data.competitions || [];

  if (competitions.length === 0) {
    return <div className="profile-empty">Aucune participation à des compétitions</div>;
  }

  return (
    <div className="profile-section">
      <h3 className="profile-section-title">Participations</h3>
      <table className="profile-stats-table">
        <thead>
          <tr>
            <th>Phase</th>
            <th style={{ textAlign: 'right' }}>Pts</th>
            <th style={{ textAlign: 'right' }}>V</th>
            <th style={{ textAlign: 'right' }}>N</th>
            <th style={{ textAlign: 'right' }}>D</th>
          </tr>
        </thead>
        <tbody>
          {competitions.map((c: {
            idparticipant: number;
            phase: { nom: string };
            division?: { nom: string };
            competition?: { nom: string };
            classement: number;
            points: number;
            victoires: number;
            nuls: number;
            defaites: number;
          }) => (
            <tr key={c.idparticipant}>
              <td className="stat-label">
                {c.competition?.nom || c.phase?.nom}
                {c.division?.nom && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{c.division.nom}</span>}
              </td>
              <td className="stat-value">{fmtNum(c.points)}</td>
              <td className="stat-value">{fmtNum(c.victoires)}</td>
              <td className="stat-value">{fmtNum(c.nuls)}</td>
              <td className="stat-value">{fmtNum(c.defaites)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
